import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useMemo } from 'react';

import { CartItem, isCartItemAvailable } from '@/entities/cart';
import { CounterSimple } from '@/features/counter';
import { formatPrice, getProductUrl } from '@/shared/lib';
import { Dropdown, Icon } from '@/shared/ui';

import styles from './cartItemExtended.module.scss';

interface CartItemExtendedProps {
  item: CartItem;
  onRemove: (arg: number, arg2: number) => void;
  onQuantityChange: (delta: -1 | 1) => void;
  onPriceChange: (newPriceId: number) => void;
}

const CartItemExtended: FC<CartItemExtendedProps> = (props) => {
  const { item, onRemove, onQuantityChange, onPriceChange } = props;
  const { product, selectedPrice, quantity } = item;
  const isAvailable = isCartItemAvailable(item);
  const priceOptions = useMemo(() => {
    return (
      product.prices?.map((price) => {
        return {
          value: price.id,
          label: price.name,
        };
      }) || []
    );
  }, [product]);

  const handleQuantityIncrease = () => {
    if (!isAvailable) {
      return;
    }
    onQuantityChange(1);
  };

  const handleQuantityDecrease = () => {
    onQuantityChange(-1);
  };

  const handlePriceChange = (newPriceId: string | number) => {
    onPriceChange(Number(newPriceId));
  };

  return (
    <article
      className={classNames(
        styles.container,
        !isAvailable && styles.container_unavailable,
      )}
    >
      <Link
        className={styles.container__imageBlock}
        href={getProductUrl(product)}
      >
        <Image
          fill
          alt={product.name}
          className={styles.container__image}
          src={product.image}
          style={{ objectFit: 'cover' }}
        />
      </Link>
      <div className={styles.container__content}>
        <div className={styles.container__textBlock}>
          <Link
            className={styles.container__title_link}
            href={getProductUrl(product)}
          >
            <p className={styles.container__text}>{product.name}</p>
          </Link>
          {!isAvailable && (
            <p className={styles.container__stock}>Нет в наличии</p>
          )}
        </div>
        <div className={styles.container__dropdownBlock}>
          <Dropdown
            options={priceOptions}
            value={selectedPrice.id}
            onChange={handlePriceChange}
          />
        </div>
        <div className={styles.container__counterBlock}>
          <CounterSimple
            count={item.quantity}
            decrement={handleQuantityDecrease}
            increment={handleQuantityIncrease}
            incrementDisabled={!isAvailable}
          />
        </div>
        <div className={styles.container__priceBlock}>
          <p className={styles.container__price}>
            {formatPrice(
              ((selectedPrice?.price_with_personal_discount ??
                selectedPrice?.price ??
                0) as number) * (Number.isFinite(quantity) ? quantity : 0),
            )}{' '}
            <span className={styles.container__price_old}>
              {formatPrice(
                (selectedPrice.old_price ??
                  (selectedPrice?.price_with_personal_discount
                    ? selectedPrice.price
                    : 0) ??
                  0) * (Number.isFinite(quantity) ? quantity : 0),
              )}
            </span>
          </p>
        </div>
        <div className={styles.container__counter_tablet}>
          <CounterSimple
            count={quantity}
            decrement={handleQuantityDecrease}
            increment={handleQuantityIncrease}
            incrementDisabled={!isAvailable}
          />
        </div>
      </div>
      <div className={styles.container__controls}>
        <button
          className={styles.container__btn}
          title="Удалить"
          onClick={() => onRemove(product.id, selectedPrice.id)}
        >
          <Icon name="crossBig" />
        </button>
        <div className={styles.container__counter_mobile}>
          <CounterSimple
            count={quantity}
            decrement={handleQuantityDecrease}
            increment={handleQuantityIncrease}
            incrementDisabled={!isAvailable}
          />
        </div>
      </div>
    </article>
  );
};

export default CartItemExtended;
